import { nextTick } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import { voucherDebug } from './useVoucherDebug'
import { resolveDevicePriceForCartItem } from '~/composable/voucher/resolveDevicePriceForCart'
import { useCartStore } from '~/stores/useCartStore'
import { useAppliedDiscountCodeStore } from '~/stores/voucher/useAppliedDiscountCodeStore'
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore'

/**
 * Composable for voucher cart state
 * Combines cart sync and voucher state restoration logic.
 *
 * Public API is the union of the previous useVoucherCartSync and
 * useVoucherStateRestoration composables.
 */
export const useVoucherCartState = () => {
  const cartStore = useCartStore()
  const appliedDiscountCodeStore = useAppliedDiscountCodeStore()
  const chosenVariantDetailsStore = useChosenVariantDetailsStore()

  const { getCart } = storeToRefs(cartStore)
  const { productDetails, chosenChannel } = storeToRefs(chosenVariantDetailsStore)
  const { appliedDiscountCodes, enteredVoucherCode, hasAppliedDiscountCode, totalDiscountCodeDiscount } = storeToRefs(appliedDiscountCodeStore)

  // --- Cart / product helpers (from useVoucherCartSync + useVoucherStateRestoration) ---

  const findDeviceItemInCart = (cart) => {
    if (!cart?.cartItemGroupList) {
      return null
    }
    const currentSku = productDetails.value?.chosenVariantSku
    const allItems = cart.cartItemGroupList.flatMap(group => group.cartItems || [])
    if (currentSku) {
      const matchingCurrentItem = allItems.find(item =>
        (item.cartProduct?.productTypeKey === 'device' ||
          item.cartProduct?.productTypeKey === 'accessory') &&
        item.cartProduct?.sku === currentSku
      )
      if (matchingCurrentItem) {
        return matchingCurrentItem
      }
    }
    return allItems.find(item =>
      item.cartProduct?.productTypeKey === 'device' ||
      item.cartProduct?.productTypeKey === 'accessory'
    ) || null
  }

  const getDiscountedPriceFromCart = (cart) => {
    const deviceItem = findDeviceItemInCart(cart)
    if (!deviceItem) {
      return null
    }
    const storeVoucherDiscount = hasAppliedDiscountCode.value
      ? (totalDiscountCodeDiscount.value || 0)
      : null
    return resolveDevicePriceForCartItem(deviceItem, cart, storeVoucherDiscount)
  }

  const cartContainsCurrentProduct = (cart) => {
    const currentSku = productDetails.value?.chosenVariantSku
    if (!currentSku || !cart?.cartItemGroupList) {
      return false
    }
    return cart.cartItemGroupList.some(group =>
      group.cartItems?.some(item =>
        item.cartProduct?.sku === currentSku
      )
    )
  }

  // --- Discount store sync (from useVoucherCartSync) ---

  const updateDiscountCodeStore = (updatedCart, voucherCode) => {
    if (cartContainsCurrentProduct(updatedCart)) {
      appliedDiscountCodeStore.updateFromCart(updatedCart)
      appliedDiscountCodeStore.setEnteredVoucherCode(voucherCode)
    } else {
      appliedDiscountCodeStore.setEnteredVoucherCode('')
    }
  }

  const updateCartInStore = (updatedCart) => {
    const updatedCartForStore = JSON.parse(JSON.stringify(updatedCart))
    cartStore.setCart(updatedCartForStore)
  }

  const waitForWatchersToProcess = async () => {
    await nextTick()
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  const waitForReactiveUpdates = async () => {
    await nextTick()
    await nextTick()
  }

  // --- Cart + voucher code preservation (from useVoucherCartSync) ---

  const clearEnteredVoucherCode = () => {
    appliedDiscountCodeStore.setEnteredVoucherCode('')
  }

  const hasDiscountCodes = (cart) => Array.isArray(cart?.appliedDiscountCodes) && cart.appliedDiscountCodes.length > 0

  const setVoucherCodeFromCartIfValid = (cart) => {
    const cartCode = cart.appliedDiscountCodes[0]
    if (!cartCode.code || appliedDiscountCodeStore.enteredVoucherCode) {
      return
    }
    appliedDiscountCodeStore.setEnteredVoucherCode(cartCode.code)
  }

  const preserveCodeFromCart = (cart) => {
    if (!cartContainsCurrentProduct(cart)) {
      clearEnteredVoucherCode()
      return
    }
    if (hasDiscountCodes(cart)) {
      setVoucherCodeFromCartIfValid(cart)
    } else {
      clearEnteredVoucherCode()
    }
  }

  const handleActiveVoucherSync = (cart) => {
    if (!cartContainsCurrentProduct(cart)) {
      voucherDebug('handleActiveVoucherSync: Cart does not contain current product, clearing enteredVoucherCode')
      appliedDiscountCodeStore.setEnteredVoucherCode('')
      return
    }
    appliedDiscountCodeStore.updateFromCart(cart)
    preserveCodeFromCart(cart)
  }

  const handleCartProductMismatch = async (resetVoucherState, resetInstallmentOptionsToOriginal) => {
    voucherDebug('Cart discount codes do not match current product, clearing discount codes')
    appliedDiscountCodeStore.clearDiscountCodes()
    resetVoucherState()
    await resetInstallmentOptionsToOriginal()
  }

  const handleCartProductMatch = (cart) => {
    if (!cartContainsCurrentProduct(cart)) {
      voucherDebug('handleCartProductMatch: Cart does not contain current product, clearing enteredVoucherCode')
      appliedDiscountCodeStore.setEnteredVoucherCode('')
      return
    }
    if (cart.appliedDiscountCodes && cart.appliedDiscountCodes.length > 0) {
      appliedDiscountCodeStore.updateFromCart(cart)
      preserveCodeFromCart(cart)
    } else {
      appliedDiscountCodeStore.setEnteredVoucherCode('')
    }
  }

  const handleDeviceAccessoryPageLoad = async (isComingBackFromCheckout, resetVoucherState, resetInstallmentOptionsToOriginal) => {
    voucherDebug('Device or accessory product detected on page load, clearing discount codes')
    appliedDiscountCodeStore.clearDiscountCodes()
    resetVoucherState()
    if (!isComingBackFromCheckout()) {
      await resetInstallmentOptionsToOriginal()
    }
  }

  const shouldHandleDeviceAccessoryPageLoad = (isPageLoad, isDeviceOrAccessory, hasActiveVoucherCode) =>
    isPageLoad && isDeviceOrAccessory && !hasActiveVoucherCode()

  const shouldHandleActiveVoucherSync = (isPageLoad, isDeviceOrAccessory, hasActiveVoucherCode) =>
    isDeviceOrAccessory && !isPageLoad && hasActiveVoucherCode()

  const validateCart = (cart) => !!cart && Array.isArray(cart.appliedDiscountCodes)

  const syncStoreWithCart = async (
    isPageLoad,
    isDeviceOrAccessory,
    hasActiveVoucherCode,
    resetVoucherState,
    resetInstallmentOptionsToOriginal,
    isComingBackFromCheckout
  ) => {
    const cart = getCart.value
    if (!validateCart(cart)) {
      appliedDiscountCodeStore.setEnteredVoucherCode('')
      return
    }

    if (!cartContainsCurrentProduct(cart)) {
      voucherDebug('syncStoreWithCart: Cart does not contain current product, clearing enteredVoucherCode')
      appliedDiscountCodeStore.setEnteredVoucherCode('')
      await handleCartProductMismatch(resetVoucherState, resetInstallmentOptionsToOriginal)
      return
    }

    if (shouldHandleDeviceAccessoryPageLoad(isPageLoad, isDeviceOrAccessory, hasActiveVoucherCode)) {
      await handleDeviceAccessoryPageLoad(isComingBackFromCheckout, resetVoucherState, resetInstallmentOptionsToOriginal)
      return
    }

    if (shouldHandleActiveVoucherSync(isPageLoad, isDeviceOrAccessory, hasActiveVoucherCode)) {
      handleActiveVoucherSync(cart)
      return
    }

    handleCartProductMatch(cart)
  }

  // --- Cart installment helpers (from useVoucherCartSync) ---

  const createUpdatedCartItem = (deviceItem, updatedInstallment) => {
    const minimumPrice = chosenChannel.value?.productBundlePrice?.minimumPrice
    const updatedSelectedPrice = {
      ...deviceItem.cartProduct.selectedPrice,
      selectedInstallment: updatedInstallment,
      ...(minimumPrice !== undefined && { minimumPrice })
    }
    const updatedCartProduct = {
      ...deviceItem.cartProduct,
      selectedPrice: updatedSelectedPrice
    }
    return {
      ...deviceItem,
      cartProduct: updatedCartProduct
    }
  }

  const updateCartItemInGroup = (cart, deviceItem, updatedCartItem) => {
    const cartItemGroup = cart.cartItemGroupList?.find(group =>
      group.cartItems?.some(item => item === deviceItem)
    )
    if (cartItemGroup) {
      const itemIndex = cartItemGroup.cartItems.findIndex(item => item === deviceItem)
      if (itemIndex !== -1) {
        cartItemGroup.cartItems[itemIndex] = updatedCartItem
      }
    }
  }

  const syncMinimumPriceToCart = (deviceItem) => {
    if (chosenChannel.value?.productBundlePrice?.minimumPrice === undefined) {
      return
    }
    const updatedMinimumPrice = chosenChannel.value.productBundlePrice.minimumPrice
    if (deviceItem?.cartProduct?.selectedPrice) {
      deviceItem.cartProduct.selectedPrice.minimumPrice = updatedMinimumPrice
    }
  }

  // --- State restoration helpers (from useVoucherStateRestoration) ---

  const isCommerceToolsId = (code) => {
    if (!code) {
      return false
    }
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidPattern.test(code)
  }

  const hasValidStoredCode = (storedCode) => !!storedCode && hasAppliedDiscountCode.value

  const cartHasStoredVoucher = (cart, storedCode) =>
    cart.appliedDiscountCodes?.some(code => code.code === storedCode)

  const clearStoredCode = () => {
    appliedDiscountCodeStore.setEnteredVoucherCode('')
  }

  const isValidStoredCodeFormat = (storedCode) => !isCommerceToolsId(storedCode)

  const validateCartAndProductForStoredCode = (cart, storedCode) => {
    if (!cart) {
      return false
    }

    if (!cartHasStoredVoucher(cart, storedCode)) {
      clearStoredCode()
      return false
    }

    if (!cartContainsCurrentProduct(cart)) {
      clearStoredCode()
      return false
    }

    return true
  }

  const validateStoredCodeForRestore = (storedCode, cart) => {
    if (!hasValidStoredCode(storedCode)) {
      return false
    }

    if (!validateCartAndProductForStoredCode(cart, storedCode)) {
      return false
    }

    return isValidStoredCodeFormat(storedCode)
  }

  const restoreFromStoredCode = (_voucherCode, _isApplied, setVoucherCode, setIsApplied) => {
    const storedCode = enteredVoucherCode.value
    const cart = getCart.value

    if (!validateStoredCodeForRestore(storedCode, cart)) {
      return false
    }

    setVoucherCode(storedCode)
    setIsApplied(true)
    return true
  }

  const restoreCodeFromCartCode = (cartCode, _voucherCode, _isApplied, setVoucherCode, setIsApplied) => {
    if (!cartContainsCurrentProduct(getCart.value)) {
      appliedDiscountCodeStore.setEnteredVoucherCode('')
      return false
    }
    if (cartCode.code && !isCommerceToolsId(cartCode.code)) {
      setVoucherCode(cartCode.code)
      setIsApplied(true)
      appliedDiscountCodeStore.setEnteredVoucherCode(cartCode.code)
      return true
    }
    return false
  }

  const restoreFromAppliedCodes = (voucherCode, isApplied, setVoucherCode, setIsApplied) => {
    const codes = appliedDiscountCodes.value
    if (!codes || codes.length === 0) {
      return false
    }
    if (!cartContainsCurrentProduct(getCart.value)) {
      return false
    }
    return restoreCodeFromCartCode(codes[0], voucherCode, isApplied, setVoucherCode, setIsApplied)
  }

  const restoreFromCart = (voucherCode, isApplied, setVoucherCode, setIsApplied) => {
    const cart = getCart.value
    if (!cart?.appliedDiscountCodes || cart.appliedDiscountCodes.length === 0) {
      return false
    }

    if (!cartContainsCurrentProduct(cart)) {
      return false
    }

    const cartCode = cart.appliedDiscountCodes[0]
    return restoreCodeFromCartCode(cartCode, voucherCode, isApplied, setVoucherCode, setIsApplied)
  }

  const attemptRestore = (voucherCode, isApplied, setVoucherCode, setIsApplied) => {
    if (restoreFromStoredCode(voucherCode, isApplied, setVoucherCode, setIsApplied)) {
      return true
    }
    if (restoreFromAppliedCodes(voucherCode, isApplied, setVoucherCode, setIsApplied)) {
      return true
    }
    return restoreFromCart(voucherCode, isApplied, setVoucherCode, setIsApplied)
  }

  return {
    // from cart sync
    findDeviceItemInCart,
    getDiscountedPriceFromCart,
    cartContainsCurrentProduct,
    updateDiscountCodeStore,
    updateCartInStore,
    waitForWatchersToProcess,
    waitForReactiveUpdates,
    syncStoreWithCart,
    createUpdatedCartItem,
    updateCartItemInGroup,
    syncMinimumPriceToCart,

    // from state restoration
    isCommerceToolsId,
    restoreFromStoredCode,
    restoreFromAppliedCodes,
    restoreFromCart,
    restoreCodeFromCartCode,
    attemptRestore
  }
}
